import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { StepStatus } from '@packages/global';

/**
 * ContentPanel 组件属性
 * 用于脚本生成页面的中间内容面板
 */
export interface ContentPanelProps {
  /** 当前步骤编号 */
  stepNumber: number;
  /** 步骤名称 */
  stepName: string;
  /** 步骤状态 */
  stepStatus: StepStatus;
  /** 子内容（步骤具体内容组件） */
  children: React.ReactNode;
  /** 是否正在加载 */
  isLoading?: boolean;
  /** 标题栏右侧额外操作区（状态左边） */
  actions?: React.ReactNode;
}

/**
 * 获取步骤状态图标
 */
function StepStatusIcon({ status, isLoading }: { status: StepStatus; isLoading?: boolean }) {
  if (isLoading) {
    return (
      <div className="w-5 h-5 flex items-center justify-center">
        <ArrowPathIcon className="w-4 h-4 text-primary animate-spin" />
      </div>
    );
  }

  switch (status) {
    case 'completed':
      return <CheckCircleIcon className="w-5 h-5 text-success" />;
    case 'in_progress':
      return (
        <div className="w-5 h-5 flex items-center justify-center">
          <ArrowPathIcon className="w-4 h-4 text-primary animate-spin" />
        </div>
      );
    case 'needs_review':
      return <ExclamationTriangleIcon className="w-5 h-5 text-warning" />;
    case 'pending':
    default:
      return (
        <div className="w-5 h-5 flex items-center justify-center">
          <div className="w-2.5 h-2.5 bg-gray-400 rounded-full" />
        </div>
      );
  }
}

/**
 * 获取步骤状态文本
 */
function getStatusText(status: StepStatus, isLoading?: boolean): string {
  if (isLoading) {
    return '加载中...';
  }

  switch (status) {
    case 'completed':
      return '已完成';
    case 'in_progress':
      return '生成中...';
    case 'needs_review':
      return '需要审查';
    case 'pending':
    default:
      return '待生成';
  }
}

/**
 * 获取步骤状态文本颜色
 */
function getStatusTextColor(status: StepStatus, isLoading?: boolean): string {
  if (isLoading) {
    return 'text-primary';
  }

  switch (status) {
    case 'completed':
      return 'text-success';
    case 'in_progress':
      return 'text-primary';
    case 'needs_review':
      return 'text-warning';
    case 'pending':
    default:
      return 'text-gray-400';
  }
}

/**
 * ContentPanel 组件
 * 
 * 实现中间内容面板，包含两个区域：
 * 1. CenterHeader: 显示步骤标题和状态
 * 2. CenterBody: 渲染传入的 children（步骤具体内容）
 * 
 * 设计参考：Pencil 设计中的 CenterCard 节点
 * - cornerRadius: 16
 * - fill: $bg-page (#0A0A0B)
 * - stroke: { align: "inside", fill: "#1F1F23", thickness: 1 }
 * - layout: vertical
 * - width: fill_container
 * - height: fill_container
 * 
 * Requirements: 6.1, 6.2
 */
export default function ContentPanel({
  stepNumber,
  stepName,
  stepStatus,
  children,
  isLoading = false,
  actions,
}: ContentPanelProps) {
  const statusText = getStatusText(stepStatus, isLoading);
  const statusColor = getStatusTextColor(stepStatus, isLoading);

  return (
    <div
      className="flex-1 flex flex-col min-w-0 h-full rounded-2xl border border-border-subtle bg-bg-page overflow-hidden"
    >
      {/* CenterHeader: 步骤标题和状态 */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-border-subtle flex-shrink-0">
        {/* 左侧：步骤编号和名称 */}
        <div className="flex items-center gap-3">
          {/* 步骤编号徽章 */}
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-bg-elevated text-[13px] font-semibold text-gray-300">
            {stepNumber}
          </span>
          {/* 步骤名称 */}
          <h2 className="text-[15px] font-semibold text-gray-50">
            {stepName}
          </h2>
        </div>

        {/* 右侧：操作区 + 步骤状态 */}
        <div className="flex items-center gap-3">
          {actions}
          <div className="flex items-center gap-2">
            <StepStatusIcon status={stepStatus} isLoading={isLoading} />
            <span className={`text-[13px] font-medium ${statusColor}`}>
              {statusText}
            </span>
          </div>
        </div>
      </header>

      {/* CenterBody: 步骤内容区域 */}
      <main className="flex-1 overflow-y-auto">
        {isLoading ? (
          /* 加载状态占位 */
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3">
              <ArrowPathIcon className="w-8 h-8 text-primary animate-spin" />
              <span className="text-sm text-gray-400">正在加载内容...</span>
            </div>
          </div>
        ) : (
          /* 实际内容 */
          <div className="p-6">
            {children}
          </div>
        )}
      </main>
    </div>
  );
}
